import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert, Card } from 'react-bootstrap'
import { formatErrorResponse } from '../utils/errorHandlers'
import { issueDestinationAPI } from '../api/issueDestinationAPI'

function IssueDestinationSubcategoryDetailsScreen() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(true)
    const [destinations, setDestinations] = useState([])
    const [subcategory, setSubcategory] = useState({
        name: '',
        full_name: '',
        contact: '',
        bill_to_address: '',
        ship_to_address: '',
        phone: '',
        email: '',
        parent_destination: '',
        parent_destination_name: '',
        is_active: true
    })

    useEffect(() => {
        async function fetchData() {
            try {
                setLoading(true)
                const [subcategoryData, destinationsData] = await Promise.all([
                    issueDestinationAPI.subcategories.getById(id),
                    issueDestinationAPI.getOptions()
                ])
                
                // Ensure all string fields are never null (convert null to empty string)
                setSubcategory({
                    ...subcategoryData,
                    name: subcategoryData.name || '',
                    full_name: subcategoryData.full_name || '',
                    contact: subcategoryData.contact || '',
                    bill_to_address: subcategoryData.bill_to_address || '',
                    ship_to_address: subcategoryData.ship_to_address || '',
                    phone: subcategoryData.phone || '',
                    email: subcategoryData.email || '',
                    parent_destination_name: subcategoryData.parent_destination_name || ''
                })
                setDestinations(destinationsData)
            } catch (error) {
                console.error('Error fetching data:', error)
                setError('Failed to load subcategory data')
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [id])

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            await issueDestinationAPI.subcategories.update(id, {
                name: subcategory.name,
                full_name: subcategory.full_name,
                contact: subcategory.contact,
                bill_to_address: subcategory.bill_to_address,
                ship_to_address: subcategory.ship_to_address,
                phone: subcategory.phone,
                email: subcategory.email,
                parent_destination: subcategory.parent_destination
            });
            navigate(`/issue-destinations/${subcategory.parent_destination}`)
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleToggleActive = async (e) => {
        e.preventDefault();
        
        if (subcategory.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this subcategory?')) {
                return;
            }
        }

        try {
            const updatedData = {
                ...subcategory,
                is_active: !subcategory.is_active
            };
            const updatedSubcategory = await issueDestinationAPI.subcategories.update(id, updatedData);
            // Ensure all string fields are never null (convert null to empty string)
            setSubcategory({
                ...updatedSubcategory,
                name: updatedSubcategory.name || '',
                full_name: updatedSubcategory.full_name || '',
                contact: updatedSubcategory.contact || '',
                bill_to_address: updatedSubcategory.bill_to_address || '',
                ship_to_address: updatedSubcategory.ship_to_address || '',
                phone: updatedSubcategory.phone || '',
                email: updatedSubcategory.email || '',
                parent_destination_name: updatedSubcategory.parent_destination_name || ''
            });
        } catch (error) {
            console.error('Error toggling subcategory status:', error);
            alert(`Failed to update subcategory: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
        }
    };

    const handleDelete = async (e) => {
        e.preventDefault();
        
        if (!window.confirm('Are you sure you want to permanently delete this subcategory? This action cannot be undone.')) {
            return;
        }

        try {
            await issueDestinationAPI.subcategories.delete(id);
            navigate(`/issue-destinations/${subcategory.parent_destination}`);
        } catch (error) {
            console.error('Error deleting subcategory:', error);
            alert(`Failed to delete subcategory: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
        }
    };

    if (loading) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </Container>
        )
    }

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h1>Subcategory Details</h1>
                    <p className="text-muted">
                        Parent: <strong>{subcategory.parent_destination_name}</strong>
                    </p>
                </Col>
                <Col xs="auto">
                    <Button 
                        variant="outline-primary"
                        onClick={() => navigate(`/issue-destinations/${subcategory.parent_destination}`)}
                    >
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to List
                    </Button>
                </Col>
            </Row>

            <Card>
                <Card.Body>
                    <Form onSubmit={submitHandler}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-4" controlId="parent_destination">
                                    <Form.Label>Parent Destination*</Form.Label>
                                    <Form.Select
                                        value={subcategory.parent_destination}
                                        onChange={(e) => setSubcategory({...subcategory, parent_destination: e.target.value})}
                                        disabled={!subcategory.is_active}
                                        style={{ opacity: subcategory.is_active ? 1 : 0.6 }}
                                        required
                                    >
                                        <option value="">Select parent destination...</option>
                                        {destinations.map(dest => (
                                            <option key={dest._id} value={dest._id}>
                                                {dest.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-4" controlId="name">
                                    <Form.Label>Name*</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter name"
                                        value={subcategory.name}
                                        onChange={(e) => setSubcategory({ ...subcategory, name: e.target.value })}
                                        disabled={!subcategory.is_active}
                                        style={{ opacity: subcategory.is_active ? 1 : 0.6 }}
                                        required
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-4" controlId="full_name">
                                    <Form.Label>Full Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter full name"
                                        value={subcategory.full_name}
                                        onChange={(e) => setSubcategory({ ...subcategory, full_name: e.target.value })}
                                        disabled={!subcategory.is_active}
                                        style={{ opacity: subcategory.is_active ? 1 : 0.6 }}
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-4" controlId="contact">
                                    <Form.Label>Contact</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter contact"
                                        value={subcategory.contact}
                                        onChange={(e) => setSubcategory({ ...subcategory, contact: e.target.value })}
                                        disabled={!subcategory.is_active}
                                        style={{ opacity: subcategory.is_active ? 1 : 0.6 }}
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-4" controlId="bill_to_address">
                                    <Form.Label>Bill To Address</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter billing address"
                                        value={subcategory.bill_to_address}
                                        onChange={(e) => setSubcategory({ ...subcategory, bill_to_address: e.target.value })}
                                        disabled={!subcategory.is_active}
                                        style={{ opacity: subcategory.is_active ? 1 : 0.6 }}
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-4" controlId="ship_to_address">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Form.Label className="mb-0">Ship To Address</Form.Label>
                                        {subcategory.bill_to_address && subcategory.is_active && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                onClick={() => setSubcategory({...subcategory, ship_to_address: subcategory.bill_to_address})}
                                                style={{ fontSize: '0.875rem', padding: '0' }}
                                            >
                                                <i className="bi bi-clipboard me-1"></i>
                                                Copy from Bill To
                                            </Button>
                                        )}
                                    </div>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter shipping address"
                                        value={subcategory.ship_to_address}
                                        onChange={(e) => setSubcategory({ ...subcategory, ship_to_address: e.target.value })}
                                        disabled={!subcategory.is_active}
                                        style={{ opacity: subcategory.is_active ? 1 : 0.6 }}
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-4" controlId="phone">
                                    <Form.Label>Phone</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter phone"
                                        value={subcategory.phone}
                                        onChange={(e) => setSubcategory({ ...subcategory, phone: e.target.value })}
                                        disabled={!subcategory.is_active}
                                        style={{ opacity: subcategory.is_active ? 1 : 0.6 }}
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-4" controlId="email">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        placeholder="Enter email"
                                        value={subcategory.email}
                                        onChange={(e) => setSubcategory({ ...subcategory, email: e.target.value })}
                                        disabled={!subcategory.is_active}
                                        style={{ opacity: subcategory.is_active ? 1 : 0.6 }}
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}

                        <Row className="mt-3">
                            <Col>
                                {subcategory.is_active ? (
                                    <>
                                        <Button 
                                            variant="primary" 
                                            type="submit" 
                                            className="me-2"
                                        >
                                            Update
                                        </Button>
                                        <Button 
                                            variant="danger"
                                            onClick={handleToggleActive}
                                            className="me-2"
                                        >
                                            Deactivate
                                        </Button>
                                        <Button 
                                            variant="outline-danger"
                                            onClick={handleDelete}
                                            className="me-2"
                                        >
                                            Delete
                                        </Button>
                                        <Button 
                                            variant="outline-primary" 
                                            onClick={() => navigate(`/issue-destinations/${subcategory.parent_destination}`)}
                                        >
                                            <i className="fas fa-arrow-left me-2"></i>
                                            Back to List
                                        </Button>
                                    </>
                                ) : (
                                    <Button 
                                        variant="success"
                                        onClick={handleToggleActive}
                                    >
                                        Activate
                                    </Button>
                                )}
                            </Col>
                        </Row>

                        {!subcategory.is_active && (
                            <Alert variant="warning" className="mt-3">
                                This subcategory is currently inactive. Activate it to make changes.
                            </Alert>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    )
}

export default IssueDestinationSubcategoryDetailsScreen